// pages/SchoolRabbisProjectDetail.js
import { useParams, Link } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { fetchWithAuth } from '../auth';
import Breadcrumbs from '../components/Breadcrumbs';
import { SkeletonCardGrid } from '../components/ui';
import Accordion from '../components/Accordion';
import { useResourceDetailModal } from '../components/ResourceDetailModalContext';

export default function SchoolRabbisProjectDetail() {
  const { mainTopicId, secondaryTopicId, id } = useParams();
  const [project, setProject] = useState(null);
  const [secondaryTopic, setSecondaryTopic] = useState(null);
  const [mainTopic, setMainTopic] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    let ignore = false;
    (async () => {
      try {
        // Get project details (includes resources nested)
        const res = await fetchWithAuth(`/api/school-rabbis-projects/${id}/`);
        if (!res.ok) throw new Error('Failed');
        const data = await res.json();
        if (!ignore) setProject(data);

        // Get secondary topic name
        const secondaryRes = await fetchWithAuth(`/api/school-rabbis-secondary-topics/${secondaryTopicId}/`);
        if (secondaryRes.ok) {
          const secondaryData = await secondaryRes.json();
          if (!ignore) setSecondaryTopic(secondaryData);
        }

        // Get main topic name
        const mainRes = await fetchWithAuth(`/api/school-rabbis-main-topics/${mainTopicId}/`);
        if (mainRes.ok) {
          const mainData = await mainRes.json();
          if (!ignore) setMainTopic(mainData);
        }
      } catch {
        if (!ignore) setError('שגיאה בטעינת פרטי הפרויקט');
      } finally {
        if (!ignore) setLoading(false);
      }
    })();
    return () => { ignore = true; };
  }, [id, secondaryTopicId, mainTopicId]);

  if (loading) {
    return (
      <div className="page-wrapper">
        <div className="h-6 w-64 bg-secondary-200 dark:bg-secondary-700 rounded animate-pulse mb-6" />
        <div className="h-8 w-32 bg-secondary-200 dark:bg-secondary-700 rounded animate-pulse mx-auto mb-8" />
        <SkeletonCardGrid count={6} />
      </div>
    );
  }

  if (error) {
    return (
      <div className="page-wrapper">
        <div className="text-center py-8">
          <p className="text-red-600 dark:text-red-400">{error}</p>
        </div>
      </div>
    );
  }

  if (!project) return null;

  const resources = (project.resources || []).filter(r => r.is_enabled);
  const { openResourceDetail } = useResourceDetailModal();

  const handleResourceClick = (resource) => {
    const hasFile = !!resource.file;
    const isExternal = !!resource.url && !hasFile;
    const downloadUrl = hasFile
      ? `/api/school-rabbis-project-resources/${resource.id}/download/`
      : resource.file || resource.url;
    if (!resource.use_detail_modal && downloadUrl) {
      window.open(downloadUrl, isExternal ? '_blank' : '_self', isExternal ? 'noopener,noreferrer' : undefined);
      return;
    }

    const modalItem = {
      id: resource.id,
      model_type: 'school_rabbis_project_resource',
      title: resource.title || 'משאב ללא כותרת',
      summary: resource.explanation || '',
      explanation: resource.explanation || '',
      author: resource.author || '',
      subjectLabel: resource.discipline_name || mainTopic?.name || 'רבני בתי ספר',
      typeLabel: resource.resource_type_label || 'משאב',
      grades: resource.grades || [],
      tags: [],
      downloadUrl,
      date: resource.created_at || '',
    };
    openResourceDetail(modalItem);
  };

  return (
    <div className="page-wrapper">
      <Breadcrumbs
        items={[
          { to: '/disciplines', label: 'תחומי דעת' },
          { to: '/school-rabbis/main-topics', label: 'רבני בתי ספר' },
          { to: `/school-rabbis/main-topics/${mainTopicId}`, label: mainTopic?.name || '...' },
          { to: `/school-rabbis/main-topics/${mainTopicId}/secondary-topics/${secondaryTopicId}`, label: secondaryTopic?.name || '...' },
          { label: project.name },
        ]}
      />

      <div className="toolbar-center">
        <h2 className="text-2xl md:text-3xl font-bold text-secondary-900 dark:text-white">
          {project.name}
        </h2>
      </div>

      <p className="text-center mt-2 mb-6">
        <Link
          to={`/school-rabbis/main-topics/${mainTopicId}/secondary-topics/${secondaryTopicId}`}
          className="text-primary-600 hover:text-primary-700 dark:text-primary-400 dark:hover:text-primary-300"
        >
          חזרה לנושא המשני
        </Link>
      </p>

      {/* Explanation accordion */}
      <Accordion content={project.explanation} className="mb-8" />

      {/* Resources */}
      {resources.length > 0 && (
        <section className="mt-8">
          <h3 className="text-xl font-semibold text-secondary-900 dark:text-white mb-4">משאבים</h3>
          <div className="grid-autofit">
            {resources.map(resource => (
              <article
                key={resource.id}
                className="group bg-white dark:bg-secondary-800 rounded-2xl shadow-card overflow-hidden transition-all duration-300 hover:shadow-card-hover hover:-translate-y-1"
              >
                <button
                  type="button"
                  onClick={() => handleResourceClick(resource)}
                  className="block w-full relative overflow-hidden bg-secondary-100 dark:bg-secondary-700"
                  style={{ aspectRatio: '3/2' }}
                >
                  {resource.image ? (
                    <img
                      src={resource.image}
                      alt={resource.title || project.name}
                      className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
                      loading="lazy"
                    />
                  ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <svg className="w-12 h-12 text-secondary-300 dark:text-secondary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                  </div>
                  )}
                </button>
                <div className="p-4 md:p-5">
                  <h4 className="text-lg font-semibold text-secondary-900 dark:text-white text-center">
                    <button
                      type="button"
                      onClick={() => handleResourceClick(resource)}
                      className="hover:text-primary-600 dark:hover:text-primary-400 transition-colors text-right w-full text-inherit"
                    >
                      {resource.title || 'משאב ללא כותרת'}
                    </button>
                  </h4>
                  {resource.author && <p className="text-sm text-secondary-500 dark:text-secondary-400 mt-1">{resource.author}</p>}
                  {resource.explanation && (
                    <p className="text-sm text-secondary-500 dark:text-secondary-400 mt-2">
                      {resource.explanation}
                    </p>
                  )}
                </div>
              </article>
            ))}
          </div>
        </section>
      )}

      {resources.length === 0 && (
        <p className="text-center text-secondary-500 dark:text-secondary-400 py-8">
          אין משאבים להצגה.
        </p>
      )}
    </div>
  );
}